import { useMemo, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'

import Group from 'components/utils/Group'
import Divider from 'LuxKit/Divider'
import AssistChip from 'LuxKit/Chips/AssistChip'
import JourneyV2Details from 'components/Flights/V2/FareFamily/JourneyV2Details'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import TripDrawer from 'tripPlanner/components/GenericDrawer/TripDrawer'
import { type TransportOption, isLeFlightSavedItemData } from 'tripPlanner/api/types'

export interface BookedShortlistedFlightDetailsSection {
  key: string
  leg: App.Leg
  inclusions: App.InclusionFeatures
  reservationInfo?: App.NDCReservationInfo
  pnrId?: string
  region?: string
  isStandalone?: boolean
  isDeparting: boolean
}

interface Props {
  option: TransportOption
  sections: Array<BookedShortlistedFlightDetailsSection>
}

const DrawerContent = styled(Group)`
  width: 100%;
  max-width: ${rem(700)};
  margin: ${rem(24)} auto;
  padding: 0 ${rem(24)};
  box-sizing: border-box;
`

const HeaderActions = styled(Group)`
  max-width: calc(100% - ${rem(44)});
`

interface SectionProps {
  section: BookedShortlistedFlightDetailsSection
}

function Section({ section }: SectionProps) {
  const [fareRulesURL, setFareRulesURL] = useState<string | undefined>(undefined)
  const [fareRulesParams, setFareRulesParams] = useState<any>(undefined)

  return (
    <JourneyV2Details
      leg={section.leg}
      inclusions={section.inclusions}
      reservationInfo={section.reservationInfo}
      pnrId={section.pnrId}
      region={section.region}
      isStandalone={section.isStandalone}
      isDeparting={section.isDeparting}
      fareRulesURL={fareRulesURL}
      fareRulesParams={fareRulesParams}
      setFareRulesURL={setFareRulesURL}
      setFareRulesParams={setFareRulesParams}
    />
  )
}

function BookedShortlistedFlightDetailsDrawer({ option, sections }: Props) {
  const modalElement = useModalElementContext()
  const intl = useIntl()

  const viewBookingLabel = intl.formatMessage({
    id: 'hQVLEOk4tU7g9fV3Vxbv',
    defaultMessage: 'View booking',
    description: 'Assist chip label in booked shortlisted flight details drawer header linking to My Escapes order view',
  })

  const savedItemData = isLeFlightSavedItemData(option.savedItemData) ? option.savedItemData : undefined
  const returnLegData = isLeFlightSavedItemData(option.returnLeg?.savedItemData) ? option.returnLeg.savedItemData : undefined
  const bookingOrderId = savedItemData?.orderId ?? returnLegData?.orderId
  const bookingUrl = bookingOrderId ? `/account/my-escapes/order/${bookingOrderId}` : undefined

  const handleClose = () => {
    modalElement.resolve()
  }

  const handleAfterClose = () => {
    modalElement.afterClose()
  }

  const content = useMemo(() => {
    if (sections.length <= 1) {
      return sections.map((section) => <Section key={section.key} section={section} />)
    }

    return sections.flatMap((section, index) => {
      const node = <Section key={section.key} section={section} />
      if (index === 0) {
        return [node, <Divider key={`${section.key}-divider`} kind="primary" />]
      }
      return [node]
    })
  }, [sections])

  const headerActions = useMemo(() => {
    if (!bookingUrl) {
      return undefined
    }

    return (
      <HeaderActions direction="horizontal" gap={8} verticalAlign="center">
        <AssistChip
          variant="filled"
          size="small"
          href={bookingUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {viewBookingLabel}
        </AssistChip>
      </HeaderActions>
    )
  }, [bookingUrl, viewBookingLabel])

  return (
    <TripDrawer
      isOpen={Boolean(modalElement.open)}
      onClose={handleClose}
      onAfterClose={handleAfterClose}
      direction="right-to-left"
      headerActions={headerActions}
    >
      <DrawerContent direction="vertical" gap={24}>
        {content}
      </DrawerContent>
    </TripDrawer>
  )
}

export default BookedShortlistedFlightDetailsDrawer
